<template>
  <div class="events-venue-page">
    <img class="demo-bg" :src="bgImage" alt="" />

    <div class="background-container">
      <v-container
        fluid
        class="box-container white-box events-venue-container"
      >
        <div v-if="showEventsFacilityPromo" class="events-form-corner-logo">
          <img
            class="events-form-corner-logo__img"
            :src="eventsCornerLogoUrl"
            alt="Fairfield City Council"
            width="180"
            height="48"
            loading="eager"
            decoding="async"
          />
        </div>
      <header v-if="showEventsFacilityPromo" class="events-form-promo-header">
        <img
          class="events-form-promo-header__img"
          :src="eventsChildSeatHeaderImageUrl"
          alt="Free child car seat safety check"
          width="1200"
          height="360"
          loading="eager"
          decoding="async"
        />
      </header>

      <h1 class="title" :style="{ backgroundColor: color }">
        {{ pageTitle }}
      </h1>

      <div v-if="status === ''">
        <p class="intro">
          Complete the form below to book your session. Fields marked with * are
          required.
        </p>

        <div class="events-venue-fields-block">
            <FormKit
              label="First name *"
              type="text"
              validation="required|length:1,100"
              placeholder="First name"
              v-model="formData.firstName"
              validation-label="First name"
            />

            <FormKit
              label="Surname *"
              type="text"
              validation="required|length:1,100"
              placeholder="Surname"
              v-model="formData.surname"
              validation-label="Surname"
            />

            <FormKit
              label="Phone number *"
              type="tel"
              validation="required|au_phone"
              :validation-rules="{ au_phone: validateAustralianPhone }"
              :validation-messages="{
                au_phone:
                  'Use an Australian number (e.g. 04xx xxx xxx or 0x xxxx xxxx).',
              }"
              placeholder="04xx xxx xxx"
              v-model="formData.phone"
              validation-label="Phone number"
              autocomplete="tel"
            />

            <FormKit
              label="Email *"
              type="email"
              validation="required|email"
              placeholder="you@example.com"
              v-model="formData.email"
              validation-label="Email"
              autocomplete="email"
            />
        </div>

        <p class="address-disclaimer">
          Your address should match the registration address.
        </p>

        <v-row class="address-row" dense align="start">
          <v-col cols="12" md="6">
            <FormKit
              label="Street address *"
              type="text"
              validation="required|length:3,200"
              placeholder="Street number and name"
              v-model="formData.streetAddress"
              validation-label="Street address"
            />
          </v-col>
          <v-col cols="12" md="6" class="suburb-postcode-col">
            <FormKit
              label="Suburb *"
              type="select"
              :options="suburbFormOptions"
              name="suburbSelect"
              validation="required"
              placeholder="Select suburb"
              v-model="formData.suburb"
              validation-label="Suburb"
            />

            <FormKit
              label="Postcode *"
              type="text"
              validation="required|matches:/^[0-9]{4}$/"
              :validation-messages="{
                matches: 'Enter a 4-digit postcode',
              }"
              placeholder="e.g. 2165"
              :attrs="{ maxlength: '4', inputmode: 'numeric', autocomplete: 'postal-code' }"
              v-model="formData.postcode"
              validation-label="Postcode"
            />
          </v-col>
        </v-row>

        <FormKit
          label="Registration number *"
          type="text"
          validation="required|length:1,20"
          placeholder="Vehicle registration"
          v-model="formData.registrationNumber"
          validation-label="Registration number"
        />

        <FormKit
          label="How many child seats do you want fitted or checked? *"
          type="select"
          :options="childSeatFormOptions"
          name="childSeatCountSelect"
          validation="required"
          placeholder="Select number of seats"
          v-model="formData.childSeatCount"
          validation-label="Number of child seats"
        />

        <FormKit
          label="Slots *"
          type="select"
          :options="slots"
          name="eventSlots"
          validation="required"
          validation-label="Slot"
          placeholder="Please select a slot"
          v-model="formData.slotId"
        />

        <section
          class="events-terms-block"
          aria-labelledby="events-terms-heading"
        >
          <h3 id="events-terms-heading" class="events-terms-heading">
            Terms and conditions
          </h3>
          <ul class="events-terms-list">
            <li>
              This service is available only to residents of the Fairfield
              Local Government Area (LGA). Proof of residential address must be
              presented at the time of fitting.
            </li>
            <li>
              All child restraint fittings must be booked in advance through the
              online booking system. Walk-ins cannot be accepted.
            </li>
            <li>
              A maximum of three (3) child restraints may be installed per
              vehicle.
            </li>
            <li>
              Each vehicle requiring child restraint installation must have its
              own separate booking slot.
            </li>
            <li>
              Child restraints will be fitted by a Transport for NSW (TfNSW)
              Authorised Child Restraint Fitter. The authorised fitter is
              responsible for the installation service and assumes liability for
              the fitting provided.
            </li>
            <li>
              The fitting service is provided free of charge. If additional parts
              or accessories are required for installation, these may be
              purchased on the day directly from the authorised fitter at the
              participant's own cost.
            </li>
            <li>
              By booking, you consent to receiving event-related information and
              updates from Fairfield City Council via email.
            </li>
            <li>
              Participants are requested to arrive on time for their allocated
              booking. Late arrivals may result in reduced fitting time or
              cancellation of the appointment.
            </li>
          </ul>
        </section>

        <FormKit
          label="I have read and accept the terms and conditions above *"
          name="acceptTerms"
          type="checkbox"
          validation="accepted"
          validation-label="terms and conditions"
          v-model="formData.acceptTerms"
        />

        <div class="button-container">
          <v-btn
            class="events-venue-submit booking-theme-btn"
            :style="{ backgroundColor: buttonColor, color: 'var(--booking-button-font-color)' }"
            :disabled="submitting || !isFormValid"
            :class="{ 'btn-faded': submitting || !isFormValid }"
            variant="text"
            :loading="submitting"
            @click="submitBooking"
          >
            Submit booking
          </v-btn>
        </div>

        <footer v-if="showEventsFacilityPromo" class="events-form-promo-footer">
          <a
            class="events-form-promo-footer__link"
            :href="eventsChildSeatFooterLinkUrl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Child Car Seats Australia for child restraint safety information (opens in a new tab)"
          >
            <img
              class="events-form-promo-footer__img"
              :src="eventsChildSeatFooterImageUrl"
              alt=""
              width="1200"
              height="360"
              loading="lazy"
              decoding="async"
            />
          </a>
        </footer>
      </div>
    </v-container>

    <div v-if="status === 'success'" class="message-box _success">
      <i class="fa fa-check-circle" aria-hidden="true"></i>
      <h2>Booking received</h2>
      <p v-if="successMessage">{{ successMessage }}</p>
      <p v-else>
        Thank you. Your booking request has been submitted. You will receive
        confirmation details if applicable.
      </p>
      <p v-if="successSlot"><strong>Slot:</strong> {{ successSlot }}</p>
      <div>
        <a class="success-button" :href="homePageHref" role="button">
          Back to home
        </a>
      </div>
    </div>

    <div v-if="status === 'failed'" class="message-box _error">
      <i class="fa-solid fa-circle-xmark"></i>
      <h2>Something went wrong</h2>
      <p>{{ failMessage }}</p>
      <div>
        <a class="_error-button" :href="homePageHref" role="button">
          Back to home
        </a>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { GetEventsBookingSlots, submitEventsBooking } from "../../../../server/config";
import { getBookingGeneral } from "../../../store/vuexStore";
import { isEventsVenueVenueType } from "@/utils/eventsVenueMatch.js";
import {
  suburbSelectOptions,
  CHILD_SEAT_COUNT_OPTIONS,
  FAIRFIELD_SUBURB_SET,
} from "@/constants/fairfieldSuburbs.js";
import {
  normalizeAustralianPhone,
  isValidAustralianPhone,
  isValidEmailBasic,
} from "@/utils/australianContact.js";
import {
  isChildSeatBookingSuccess,
  childSeatBookingErrorMessage,
} from "@/utils/childSeatApi.js";
import * as ENV from "@/config.js";
import {
  isEventsBookingFacility,
  EVENTS_CHILD_SEAT_FORM_HEADER_IMAGE_URL,
  EVENTS_CHILD_SEAT_FORM_FOOTER_IMAGE_URL,
  EVENTS_CHILD_SEAT_FORM_FOOTER_LINK_URL,
  EVENTS_FORM_CORNER_LOGO_URL,
} from "@/constants/eventsFacilityPromo.js";
import { applyBookingTheme } from "@/utils/bookingTheme.js";

export default {
  name: "EventsVenueBooking",
  data() {
    return {
      bgImage: "",
      color: "",
      buttonColor: "",
      pageTitle: "Book a session",
      slots: [],
      suburbFormOptions: suburbSelectOptions(),
      childSeatFormOptions: CHILD_SEAT_COUNT_OPTIONS,
      submitting: false,
      status: "",
      failMessage: "",
      successMessage: "",
      successSlot: "",
      formData: {
        firstName: "",
        surname: "",
        phone: "",
        email: "",
        streetAddress: "",
        suburb: "",
        postcode: "",
        registrationNumber: "",
        childSeatCount: "",
        slotId: "",
        acceptTerms: false,
      },
    };
  },
  computed: {
    showEventsFacilityPromo() {
      return isEventsBookingFacility();
    },
    eventsChildSeatHeaderImageUrl() {
      return isEventsBookingFacility()
        ? EVENTS_CHILD_SEAT_FORM_HEADER_IMAGE_URL
        : "";
    },
    eventsChildSeatFooterImageUrl() {
      return isEventsBookingFacility()
        ? EVENTS_CHILD_SEAT_FORM_FOOTER_IMAGE_URL
        : "";
    },
    eventsChildSeatFooterLinkUrl() {
      return isEventsBookingFacility()
        ? EVENTS_CHILD_SEAT_FORM_FOOTER_LINK_URL
        : "";
    },
    eventsCornerLogoUrl() {
      return isEventsBookingFacility() ? EVENTS_FORM_CORNER_LOGO_URL : "";
    },
    homePageHref() {
      try {
        const resolved = this.$router?.resolve?.({ path: this.homePath() });
        if (resolved && resolved.href) return resolved.href;
      } catch {
        /* ignore */
      }
      const path = this.homePath();
      if (typeof window !== "undefined") {
        try {
          return new URL(path, window.location.origin).href;
        } catch {
          /* ignore */
        }
      }
      return path;
    },
    isFormValid() {
      const f = this.formData;
      const firstName = String(f.firstName || "").trim();
      const surname = String(f.surname || "").trim();
      const street = String(f.streetAddress || "").trim();
      const suburb = String(f.suburb || "").trim();
      const postcode = String(f.postcode || "").trim();
      const rego = String(f.registrationNumber || "").trim();
      const slotId = String(f.slotId || "").trim();
      const n = Number(f.childSeatCount);

      return (
        firstName.length >= 1 &&
        firstName.length <= 100 &&
        surname.length >= 1 &&
        surname.length <= 100 &&
        isValidAustralianPhone(f.phone) &&
        isValidEmailBasic(f.email) &&
        street.length >= 3 &&
        street.length <= 200 &&
        suburb.length > 0 &&
        FAIRFIELD_SUBURB_SET.has(suburb) &&
        /^[0-9]{4}$/.test(postcode) &&
        rego.length >= 1 &&
        rego.length <= 20 &&
        Number.isFinite(n) &&
        n >= 1 &&
        n <= 3 &&
        slotId.length > 0 &&
        f.acceptTerms === true &&
        this.slots.length > 0
      );
    },
  },
  methods: {
    ...mapActions({
      bookingGetConfig: `${ENV.VUE_APP_FACILITY}_booking/bookingGetConfig`,
    }),
    homePath() {
      return `/bookingapp/${ENV.VUE_APP_FACILITY}/`;
    },
    setBranding() {
      const data = getBookingGeneral() || {};
      applyBookingTheme(data);
      this.bgImage = data?.homepageimage || "";
      this.color = data?.color || "#2D6468";
      this.buttonColor = data?.buttoncolor || "#0275FF";
      this.pageTitle = data?.header?.title || "Book a session";
    },
    formatFullAddress() {
      const street = String(this.formData.streetAddress || "").trim();
      const suburb = String(this.formData.suburb || "").trim();
      const postcode = String(this.formData.postcode || "").trim();
      if (!street || !suburb || !postcode) return "";
      return `${street}, ${suburb}, ${postcode}`;
    },
    validateAustralianPhone(node) {
      const v = node?.value;
      if (v == null || String(v).trim() === "") return true;
      return isValidAustralianPhone(v);
    },
    async loadSlots() {
      try {
        this.slots = await GetEventsBookingSlots();
        if (!this.slots.length) {
          this.$toast?.warning?.(
            "No slots are available right now. Please try again later.",
            { position: "top-right" }
          );
        }
      } catch {
        this.slots = [];
        this.$toast?.error?.("Could not load available slots.", {
          position: "top-right",
        });
      }
    },
    validateVenueQuery() {
      const q = this.$route.query.venueType;
      if (!q || !isEventsVenueVenueType(q)) {
        this.$router.replace({ path: this.homePath() });
        return false;
      }
      return true;
    },
    buildChildSeatFreeBookingPayload() {
      const n = Number(this.formData.childSeatCount);
      return {
        acceptTerms: Boolean(this.formData.acceptTerms),
        address: this.formatFullAddress(),
        carRegistrationNumber: String(this.formData.registrationNumber || "").trim(),
        childSeatCount: Number.isFinite(n) ? n : 0,
        email: String(this.formData.email || "").trim(),
        firstName: String(this.formData.firstName || "").trim(),
        phoneNumber: normalizeAustralianPhone(this.formData.phone),
        slotId: String(this.formData.slotId || "").trim(),
        surname: String(this.formData.surname || "").trim(),
      };
    },
    async submitBooking() {
      if (!this.formData.slotId || !this.formData.acceptTerms) {
        this.$toast?.warning?.(
          !this.formData.slotId
            ? "Please select a slot."
            : "Please accept the terms and conditions.",
          { position: "top-right" }
        );
        return;
      }

      this.submitting = true;
      this.failMessage = "";

      try {
        const payload = this.buildChildSeatFreeBookingPayload();
        const res = await submitEventsBooking(payload);

        if (isChildSeatBookingSuccess(res)) {
          const d = res.data || {};
          this.successMessage = String(d.message || "").trim();
          this.successSlot = String(d.slot || "").trim();
          this.status = "success";
          return;
        }

        this.status = "failed";
        this.failMessage = childSeatBookingErrorMessage(res?.data, res?.status);
      } catch {
        this.status = "failed";
        this.failMessage =
          "Unable to reach the server. Check your connection and try again.";
      } finally {
        this.submitting = false;
      }
    },
  },
  async created() {
    if (!isEventsBookingFacility()) {
      this.$router.replace({ path: this.homePath() });
      return;
    }
    if (!ENV.VUE_APP_EVENTS_VENUE_NAME || !String(ENV.VUE_APP_EVENTS_VENUE_NAME).trim()) {
      this.$router.replace({ path: this.homePath() });
      return;
    }
    if (!this.validateVenueQuery()) return;

    await this.bookingGetConfig();
    this.setBranding();
    await this.loadSlots();
  },
};
</script>

<style src="../formSummaryPage/formSummaryPage.css"></style>
<style src="./eventsVenueBooking.css"></style>
